import React, { useState } from 'react';
import { Button, Input, Toaster } from '../../components/common';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { MdDone } from 'react-icons/md';
import { FcGoogle } from 'react-icons/fc';
import { passwordErrorMessages } from '../../lib/constants.tsx';
import { getPasswordStrength } from '../../utils/general.ts';
import toast from 'react-hot-toast';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const SignUpForm = () => {
  const { signUp, googleLogin, error } = useAuth();

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const disabledSubmit =
    watch('firstName') === '' ||
    watch('lastName') === '' ||
    watch('email') === '' ||
    watch('password') === '' ||
    watch('confirmPassword') === '';

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);

    if (data.password !== data.confirmPassword) {
      toast.custom(t => (
        <Toaster
          message='Passwords do not match, Please try again.'
          type='error'
          id={t.id}
        />
      ));
      setError('password', { message: '' });
      setError('confirmPassword', { message: '' });

      setIsLoading(false);
      return;
    }

    const payload = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
      password: data.password,
    };

    try {
      const response = await signUp(payload);
      if (response?.code === 409) {
        setError('email', { message: response?.message as string });
        return;
      }
      return;
    } catch (error) {
      console.error('Sign up failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className='w-full' onSubmit={handleSubmit(onSubmit)}>
      {error && (
        <div className='rounded-md bg-red-50 p-4 mb-3'>
          <div className='text-sm text-red-700'>{error}</div>
        </div>
      )}

      <div className='flex flex-wrap gap-2'>
        <div className='w-[calc(50%-0.25rem)] mb-4'>
          <Input
            label='First name'
            autoComplete='first-name'
            placeholder='First name'
            required
            {...register('firstName', {
              required: 'First name is required',
            })}
          />
        </div>

        <div className='w-[calc(50%-0.25rem)] mb-4'>
          <Input
            label='Last name'
            autoComplete='last-name'
            placeholder='Last name'
            required
            {...register('lastName', {
              required: 'Last name is required',
            })}
          />
        </div>

        <div className='w-full mb-4'>
          <Input
            label='Email ID'
            type='email'
            autoComplete='email'
            placeholder='Enter email ID'
            required
            helperText={errors.email?.message as string}
            color={errors.email ? 'error' : 'default'}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Please enter a valid email',
              },
            })}
          />
        </div>

        <div className='w-full mb-4'>
          <Input
            label='Password'
            type='password'
            autoComplete='current-password'
            placeholder='Enter password'
            required
            color={errors.password ? 'error' : 'default'}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: passwordErrorMessages.CHARACTERS,
              },
              pattern: {
                value: /^(?=.*[0-9])(?=.*[^A-Za-z0-9]).+$/,
                message: passwordErrorMessages.ALPHANUMERIC,
              },
              validate: value => {
                if (
                  value
                    .toLowerCase()
                    .includes(getValues('firstName').toLowerCase()) ||
                  value
                    .toLowerCase()
                    .includes(getValues('lastName').toLowerCase()) ||
                  value === getValues('email')
                ) {
                  return passwordErrorMessages.CONTAIN_NAME;
                }
                if (getPasswordStrength(value) === 'Weak') {
                  return passwordErrorMessages.WEAK;
                }
              },
              onChange: () => {
                clearErrors('password');
                clearErrors('confirmPassword');
              },
            })}
          />

          {watch('password') && (
            <div className='text-xs text-gray-500 mt-2'>
              <p
                className={`flex items-center gap-2 mb-2 font-semibold ${getPasswordStrength(watch('password')) === 'Strong' ? 'text-green-600' : getPasswordStrength(watch('password')) === 'Medium' ? 'text-yellow-600' : 'text-red-500'}`}
              >
                <MdDone size={16} /> Password strength:{' '}
                {getPasswordStrength(watch('password'))}
              </p>
              <p
                className={`flex items-center gap-2 mb-2 font-semibold ${errors.password?.message === passwordErrorMessages.CONTAIN_NAME ? 'text-red-500' : 'text-green-600'}`}
              >
                <MdDone size={16} /> Cannot contain your name or email address
              </p>
              <p
                className={`flex items-center gap-2 mb-2 font-semibold ${errors.password?.message === passwordErrorMessages.CHARACTERS ? 'text-red-500' : 'text-green-600'}`}
              >
                <MdDone size={16} /> At least 8 characters
              </p>
              <p
                className={`flex items-center gap-2 mb-0 font-semibold ${errors.password?.message === passwordErrorMessages.ALPHANUMERIC ? 'text-red-500' : 'text-green-600'}`}
              >
                <MdDone size={16} /> Contains a number or symbol
              </p>
            </div>
          )}
        </div>

        <div className='w-full mb-6'>
          <Input
            label='Confirm Password'
            type='password'
            autoComplete='confirm-password'
            placeholder='Enter confirm password'
            required
            color={errors.confirmPassword ? 'error' : 'default'}
            helperText={errors.confirmPassword?.message as string}
            {...register('confirmPassword', {
              required: 'Confirm password is required',
              onChange: () => {
                clearErrors('password');
                clearErrors('confirmPassword');
              },
            })}
          />
        </div>
      </div>

      <Button
        type='submit'
        isLoading={isLoading}
        disabled={disabledSubmit}
        className='group relative w-full'
      >
        Create account
      </Button>

      <div className='my-4 px-10'>
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-300' />
          </div>
          <div className='relative flex justify-center text-sm'>
            <span className='px-2 bg-white text-gray-500 text-xs'>OR</span>
          </div>
        </div>
      </div>

      <Button
        type='button'
        variant='outline'
        className='w-full'
        onClick={() => googleLogin()}
      >
        <FcGoogle size={24} /> Continue with Google
      </Button>

      <p className='mb-0 text-gray-400 font-semibold text-xs text-center w-[70%] mx-auto mt-6'>
        By signing up to create an account you are accepting{' '}
        <Link to={'#'}>Terms of use</Link> &{' '}
        <Link to={'#'}>Privacy Policy</Link>.
      </p>
    </form>
  );
};

export default SignUpForm;
